import { StateCreator } from 'zustand'

import {
  AuctionLotSold,
  LotBid,
  LotCancelBid,
  LotFinished,
  LotInfo,
  LotTimeExtended,
  UserOutbidEvent,
} from '@bonhams1793/bonhams-typescript'

export type LotChannelLastPayload =
  | ({ type: LotInfo['type'] } & LotInfo['data'])
  | ({ type: LotBid['type'] } & LotBid['data'])
  | ({ type: LotTimeExtended['type'] } & LotTimeExtended['data'])
  | ({ type: LotCancelBid['type'] } & LotCancelBid['data'])
  | ({ type: LotFinished['type'] } & LotFinished['data'])
  | null

export type UserChannelLastPayload =
  | ({ type: UserOutbidEvent['type'] } & UserOutbidEvent['data'])
  | null

export type AuctionChannelLastPayload =
  | ({ type: AuctionLotSold['type'] } & AuctionLotSold['data'])
  | null

export interface ChannelState {
  registrationsCount: number
  lastPayload:
    | LotChannelLastPayload
    | UserChannelLastPayload
    | AuctionChannelLastPayload
  subscriptionStatus: 'UNSUBSCRIBED' | 'SUBSCRIBED' | 'FAILED'
}

export interface ChannelsState {
  channels: Record<string, ChannelState>
}

export interface ChannelActions {
  addRegistration: (channelName: string) => number
  removeRegistration: (channelName: string) => number
  updateChannelPayload: (props: {
    channelName: string
    payload: ChannelState['lastPayload']
  }) => void
  updateChannelSubscriptionStatus: (props: {
    channelName: string
    status: ChannelState['subscriptionStatus']
  }) => void
}

export type ChannelSlice = ChannelsState & ChannelActions

export const initialChannelsState = { channels: {} }
const initialChannelState: ChannelState = {
  registrationsCount: 0,
  lastPayload: null,
  subscriptionStatus: 'UNSUBSCRIBED',
}

export const createChannelSlice: StateCreator<ChannelSlice> = (set) => ({
  ...initialChannelsState,
  addRegistration: (channelName) => {
    let count = 0

    set((state) => {
      const channel: ChannelState =
        state.channels[channelName] ?? initialChannelState

      count = channel.registrationsCount + 1

      return {
        channels: {
          ...state.channels,
          [channelName]: {
            ...channel,
            registrationsCount: count,
          },
        },
      }
    })

    return count
  },
  removeRegistration: (channelName) => {
    let count = 0

    set((state) => {
      const channel = state.channels[channelName]
      if (!channel) return state

      count = channel.registrationsCount - 1

      if (!count) {
        count = 0
        delete state.channels[channelName]
        return { channels: { ...state.channels } }
      }

      return {
        channels: {
          ...state.channels,
          [channelName]: {
            ...channel,
            registrationsCount: count,
          },
        },
      }
    })

    return count
  },
  updateChannelPayload: ({ channelName, payload }) =>
    set((state) => ({
      channels: {
        ...state.channels,
        [channelName]: {
          ...state.channels[channelName],
          lastPayload: payload,
        },
      },
    })),
  updateChannelSubscriptionStatus: ({ channelName, status }) =>
    set((state) =>
      state.channels[channelName]
        ? {
            channels: {
              ...state.channels,
              [channelName]: {
                ...state.channels[channelName],
                subscriptionStatus: status,
              },
            },
          }
        : state
    ),
})
